import { rmsAndDbfsInt16 } from "./noiseAnalysis";

/**
 * Heuristic "voice-like" detector for barge-in.
 *
 * Goal: reduce false barge-in from brief/impulsive noises (clicks, claps) and
 * some steady noise, while keeping real speech interruption responsive.
 *
 * NOTE: This is NOT a full speech classifier. For perfect behavior across all
 * environments you'd use a proper VAD/ML model. This is a lightweight,
 * dependency-free approximation.
 */
export class VoiceBargeInDetector {
  private readonly minStartFrames: number;
  private readonly minEndFrames: number;
  private readonly dbAboveSilenceStart: number;
  private readonly zcrMin: number;
  private readonly zcrMax: number;
  private readonly maxCrestFactor: number;
  private readonly minPitchCorrStart: number;
  private readonly minPitchCorrContinue: number;

  private voiceFrames: number = 0;
  private nonVoiceFrames: number = 0;
  private active: boolean = false;

  constructor(opts?: {
    minStartFrames?: number;
    minEndFrames?: number;
    dbAboveSilenceStart?: number;
    zcrMin?: number;
    zcrMax?: number;
    maxCrestFactor?: number;
    minPitchCorrStart?: number;
    minPitchCorrContinue?: number;
  }) {
    // Default tuning is intentionally conservative to avoid false barge-in.
    this.minStartFrames = opts?.minStartFrames ?? 7;
    this.minEndFrames = opts?.minEndFrames ?? 8;
    this.dbAboveSilenceStart = opts?.dbAboveSilenceStart ?? 8;
    this.zcrMin = opts?.zcrMin ?? 0.015;
    this.zcrMax = opts?.zcrMax ?? 0.16;
    this.maxCrestFactor = opts?.maxCrestFactor ?? 5;
    this.minPitchCorrStart = opts?.minPitchCorrStart ?? 0.34;
    this.minPitchCorrContinue = opts?.minPitchCorrContinue ?? 0.24;
  }

  reset() {
    this.voiceFrames = 0;
    this.nonVoiceFrames = 0;
    this.active = false;
  }

  isActive() {
    return this.active;
  }

  /**
   * @param chunk Int16 PCM
   * @param vadSilenceDbfs Dynamic "silence" threshold (from NoiseAnalyzer)
   */
  process(chunk: Int16Array, vadSilenceDbfs: number): { voiceActive: boolean } {
    if (!chunk || chunk.length === 0) {
      this.voiceFrames = 0;
      this.nonVoiceFrames++;
      if (this.active && this.nonVoiceFrames >= this.minEndFrames) {
        this.active = false;
      }
      return { voiceActive: this.active };
    }

    const { rms, dbfs } = rmsAndDbfsInt16(chunk);

    // Peak amplitude for crest factor (impulse rejection)
    let peak = 0;
    let zc = 0;
    let prev = chunk[0];
    for (let i = 1; i < chunk.length; i++) {
      const v = chunk[i];
      const av = Math.abs(v);
      if (av > peak) peak = av;
      if ((prev > 0 && v < 0) || (prev < 0 && v > 0)) zc++;
      prev = v;
    }
    const zcr = zc / chunk.length;

    const crestFactor = rms > 0 ? peak / 32768 / rms : Infinity;

    const loudEnough = dbfs >= vadSilenceDbfs + this.dbAboveSilenceStart;
    const zcrOk = zcr >= this.zcrMin && zcr <= this.zcrMax;
    const notImpulse = crestFactor <= this.maxCrestFactor;

    const pitchCorr = this.pitchCorrVoiced24k(chunk);

    const startCandidate =
      loudEnough && notImpulse && pitchCorr >= this.minPitchCorrStart;
    const continueCandidate =
      loudEnough &&
      notImpulse &&
      (pitchCorr >= this.minPitchCorrContinue || zcrOk);

    const isVoiceCandidate = this.active ? continueCandidate : startCandidate;

    if (isVoiceCandidate) {
      this.voiceFrames++;
      this.nonVoiceFrames = 0;
      if (!this.active && this.voiceFrames >= this.minStartFrames) {
        this.active = true;
      }
    } else {
      this.voiceFrames = 0;
      this.nonVoiceFrames++;
      if (this.active && this.nonVoiceFrames >= this.minEndFrames) {
        this.active = false;
      }
    }

    return { voiceActive: this.active };
  }

  /**
   * Lightweight voiced / periodicity score.
   * Uses downsample-by-2 normalized autocorrelation to keep CPU low.
   *
   * Returns max normalized correlation in the 80–300 Hz pitch band.
   */
  private pitchCorrVoiced24k(chunk: Int16Array): number {
    const n = Math.floor(chunk.length / 2);
    if (n < 120) return 0;

    let energy = 0;
    for (let i = 0; i < n; i++) {
      const x = chunk[i * 2];
      energy += x * x;
    }
    if (energy <= 0) return 0;

    const minLag = 40;
    const maxLag = Math.min(150, n - 2);
    if (maxLag <= minLag) return 0;

    let best = 0;

    for (let lag = minLag; lag <= maxLag; lag += 2) {
      let sum = 0;
      let e1 = 0;
      for (let i = 0; i < n - lag; i++) {
        const a = chunk[i * 2];
        const b = chunk[(i + lag) * 2];
        sum += a * b;
        e1 += b * b;
      }
      if (e1 <= 0) continue;
      const corr = sum / Math.sqrt(energy * e1);
      if (corr > best) best = corr;
    }

    if (!Number.isFinite(best) || best < 0) return 0;
    return best > 1 ? 1 : best;
  }
}
